import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DynamicForm } from '@/components/common/DynamicForm';
import { Card } from '@/components/ui/Card';
import { kpiEntity } from '@/config/entities/kpis';
import { createCRUDService } from '@/services/genericCRUD';
import { FormData } from '@/types/crud';
import { useAuth } from '@/state/auth/AuthProvider';
import toast from 'react-hot-toast';
import { useLanguage } from '@/state/language/LanguageProvider';
import { KpiThresholdOverride } from '@/components/kpis/KpiThresholdOverride';
import { parseThresholdDraft, type ThresholdDraft } from '@/components/kpis/KpiThresholdFields';

const kpiCRUDService = createCRUDService(kpiEntity);

export const KPIFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { t } = useLanguage();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<FormData>({});
  const [overrideOn, setOverrideOn] = useState(false);
  const [overrideDraft, setOverrideDraft] = useState<ThresholdDraft>({ yellowOffPct: '0', redOffPct: '20' });

  const isPlatformAdmin = user?.role === 'PLATFORM_ADMIN';

  useEffect(() => {
    if (isEditing) {
      loadKPI();
    }
  }, [id]);

  const loadKPI = async () => {
    try {
      setLoading(true);
      const kpi = await kpiCRUDService.getById(id!);
      setInitialData({
        ...kpi,
        tags: Array.isArray(kpi.tags) ? kpi.tags.join(', ') : kpi.tags || ''
      });
      const yellow = kpi.yellow_off_pct;
      const red = kpi.red_off_pct;
      const hasOverride = yellow !== null && yellow !== undefined && red !== null && red !== undefined;
      setOverrideOn(hasOverride);
      if (hasOverride) {
        setOverrideDraft({ yellowOffPct: String(yellow), redOffPct: String(red) });
      }
    } catch (err) {
      console.error('Error loading KPI:', err);
      toast.error('Failed to load KPI');
      navigate('/kpis');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: FormData) => {
    try {
      if (isPlatformAdmin) {
        data.is_universal = true;
      } else {
        data.is_universal = false;
      }
      delete data.functional_hierarchy_id;

      if (overrideOn) {
        const parsed = parseThresholdDraft(overrideDraft);
        if (!parsed) {
          toast.error(
            t('kpi.thresholdInvalid') ||
              'Yellow and red must both be 0–100, and yellow cannot be larger than red.'
          );
          return;
        }
        data.yellow_off_pct = parsed.yellowOffPct;
        data.red_off_pct = parsed.redOffPct;
      } else {
        data.yellow_off_pct = null;
        data.red_off_pct = null;
      }

      if (isEditing) {
        await kpiCRUDService.update(id!, data);
        toast.success('KPI updated successfully');
      } else {
        await kpiCRUDService.create(data);
        toast.success('KPI created successfully');
      }
      navigate('/kpis');
    } catch (err) {
      console.error('Error submitting KPI:', err);
      toast.error(isEditing ? 'Failed to update KPI' : 'Failed to create KPI');
    }
  };

  const handleCancel = () => {
    navigate('/kpis');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">
            {isEditing ? 'Edit KPI' : 'Create KPI'}
          </h1>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="text-[var(--color-secondary-text)]">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">
          {isEditing ? 'Edit KPI' : 'Create KPI'}
        </h1>
        <p className="text-[var(--color-secondary-text)]">
          {isEditing
            ? 'Update your KPI details'
            : isPlatformAdmin
              ? 'Create a new Key Performance Indicator for the library (available to all tenants)'
              : 'Create a new Key Performance Indicator for your tenant'}
        </p>
        {!isEditing && (
          <div className="mt-2 p-3 bg-[var(--color-info)]/10 border border-[var(--color-info)]/20 rounded-md">
            <p className="text-sm text-[var(--color-info)]">
              {isPlatformAdmin
                ? '🔒 Platform Admin Mode: Creating universal KPI for the library'
                : '🏢 Tenant Mode: Creating KPI specific to your organization'}
            </p>
          </div>
        )}
      </div>

      <Card>
        <div className="p-4">
          <DynamicForm
            entity={kpiEntity}
            initialData={initialData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
            isEditing={isEditing}
            extraFields={
              <KpiThresholdOverride
                enabled={overrideOn}
                draft={overrideDraft}
                onEnabled={setOverrideOn}
                onChange={setOverrideDraft}
                t={t}
              />
            }
          />
        </div>
      </Card>
    </div>
  );
};
